import type { CueCategory } from '@ohear/shared'
/**
 * TanStack Query hooks for the cue-list endpoints.
 *
 * Returns a flat ordered list of cues for a batch + scene metadata so the
 * ScriptPanel can render scene dividers without a second request.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '../api'

/** Reference waveform peaks for cue rendering. */
export interface CueRefWaveform {
  /** Sample-rate-independent peak samples in [-1, 1]. */
  peaks: number[]
  /** Number of samples per peak entry (for reconstructing time axis). */
  samples_per_peak: number
}

export interface SceneRef {
  scene_number: number
  /** Display label, e.g. "城门夜战" / "Gate Night Battle". */
  label: string
}

export type CueQcSummary = 'pass' | 'warn' | 'fail' | null

/**
 * One cue row as the actor sees it: text + timecode + my-or-not + qc/pickup.
 * Other characters' cues come back too (greyed in the script panel) so we can
 * render scenes in narrative order.
 */
export interface ActorCue {
  cue_id: string
  cue_number: number
  scene_number: number
  /** Character that speaks this line. May or may not be the current actor. */
  character_code: string
  character_id: string
  /** Cue text in the project's source language. */
  line_text: string
  /** in/out timestamps relative to the episode video, in seconds. */
  in_sec: number
  out_sec: number
  /** Predictive dynamic category for this cue (drives PredictiveHint). */
  category: CueCategory
  /** Reference voice clip URL (R2 presigned, ~5 minute TTL). */
  ref_voice_url: string | null
  /** Pre-computed peaks for the reference voice. */
  ref_waveform: CueRefWaveform | null
  /** True iff this cue belongs to the actor (mine_today). */
  is_mine: boolean
  /**
   * Highest take version delivered for this cue by the actor. Drives the
   * "approved / in-progress / not-yet-recorded" colouring in ScriptPanel.
   */
  latest_take_status: 'none' | 'recorded' | 'approved' | 'pickup_requested'
  /** Server-set when supervisor has requested a pickup. */
  pickup_note: string | null
  pickup_supervisor_name: string | null
  /** Latest take's QC summary, if any. */
  qc_summary: CueQcSummary
}

export interface BatchCueList {
  batch_id: string
  batch_number: number
  /** Episode-level scenes referenced by the cues. */
  scenes: SceneRef[]
  cues: ActorCue[]
  /** Cue ids the supervisor has flagged for pickup. */
  pickup_queue: string[]
  /** Frame rate used for SMPTE display. */
  frame_rate: { fps: number; drop_frame: boolean }
}

const batchCuesKey = (projectId: string, batchId: string) =>
  ['actor', 'project', projectId, 'batch', batchId, 'cues'] as const

/** Cue list for one batch, including pickup queue and scene labels. */
export function useBatchCues(projectId: string, batchId: string | null) {
  return useQuery({
    queryKey: batchCuesKey(projectId, batchId ?? '__none'),
    queryFn: () => api<BatchCueList>(`/api/v1/actor/projects/${projectId}/batches/${batchId}/cues`),
    enabled: Boolean(projectId && batchId),
  })
}
